import { EvalError } from './Error';
export class NodeBase {
}
export class AssignableNode extends NodeBase {
    constructor() {
        super(...arguments);
        this.isAssignable = true;
    }
}
export class NonAssignableNode extends NodeBase {
    constructor() {
        super(...arguments);
        this.isAssignable = false;
    }
}
export class AccessKeyed extends AssignableNode {
    constructor(position, object, key) {
        super();
        this.position = position;
        this.object = object;
        this.key = key;
        this.type = 'accessKeyed';
    }
    assign(scope, value) {
        const instance = this.getValidInstance(scope);
        if (Array.isArray(instance)) {
            instance[this.getArrayIndex(scope)] = value;
        }
        else {
            instance[this.getKey(scope)] = value;
        }
        return value;
    }
    evaluate(scope) {
        const instance = this.getValidInstance(scope);
        if (Array.isArray(instance)) {
            return instance[this.getArrayIndex(scope)];
        }
        return instance[this.getKey(scope)];
    }
    getArrayIndex(scope) {
        const key = this.key.evaluate(scope);
        let index;
        if (typeof key === 'number') {
            index = key;
        }
        else if (typeof key === 'string') {
            index = parseInt(key, 10);
        }
        if (index === undefined || Number.isNaN(index)) {
            throw new EvalError(this.position, `non-numeric array key: "${this.key}"`);
        }
        return index;
    }
    getKey(scope) {
        const key = this.key.evaluate(scope);
        if (typeof key === 'string') {
            return key;
        }
        else if (key !== undefined && key !== null) {
            return key.toString();
        }
        throw new EvalError(this.position, `invalid key: ${this.key}`);
    }
    getValidInstance(scope) {
        const instance = this.object.evaluate(scope);
        if (instance === null) {
            throw new EvalError(this.position, `can't access property "${this.key}" of null`);
        }
        else if (instance === undefined) {
            throw new EvalError(this.position, `can't access property "${this.key}" of undefined`);
        }
        else if (!Array.isArray(instance) && typeof instance !== 'object') {
            throw new EvalError(this.position, `can't access property "${this.key}" of ${typeof instance} value`);
        }
        return instance;
    }
}
export class AccessMember extends AssignableNode {
    constructor(position, object, name) {
        super();
        this.position = position;
        this.object = object;
        this.name = name;
        this.type = 'accessMember';
    }
    assign(scope, value) {
        const instance = this.object.evaluate(scope);
        if (instance === null) {
            throw new EvalError(this.position, `can't access property "${this.name}" of null`);
        }
        else if (instance === undefined) {
            throw new EvalError(this.position, `can't access property "${this.name}" of undefined`);
        }
        else if (Array.isArray(instance)) {
            throw new EvalError(this.position, `can't access property "${this.name}" of array`);
        }
        else if (typeof instance !== 'object') {
            throw new EvalError(this.position, `can't access property "${this.name}" of primitive value`);
        }
        instance[this.name] = value;
        return value;
    }
    evaluate(scope) {
        const instance = this.object.evaluate(scope);
        if (instance === null || instance === undefined)
            return instance;
        if (Array.isArray(instance))
            return undefined;
        if (typeof instance === 'object')
            return instance[this.name];
        if (typeof instance === 'string') {
            if (this.name === 'length')
                return instance.length;
        }
        return undefined;
    }
}
export class AccessScope extends AssignableNode {
    constructor(position, name) {
        super();
        this.position = position;
        this.name = name;
        this.type = 'accessScope';
    }
    assign(scope, value) {
        scope[this.name] = value;
        return value;
    }
    evaluate(scope) {
        return scope[this.name];
    }
}
export class Assignment extends AssignableNode {
    constructor(position, target, value) {
        super();
        this.position = position;
        this.target = target;
        this.value = value;
        this.type = 'assignment';
    }
    assign(scope, value) {
        return this.target.assign(scope, value);
    }
    evaluate(scope) {
        return this.target.assign(scope, this.value.evaluate(scope));
    }
}
export class Binary extends AssignableNode {
    constructor(position, operation, left, right) {
        super();
        this.position = position;
        this.operation = operation;
        this.left = left;
        this.right = right;
        this.type = 'binary';
    }
    assign(scope, value) {
        throw new EvalError(this.position, `can't assign to binary operation`);
    }
    evaluate(scope) {
        var _a, _b;
        const left = this.left.evaluate(scope);
        switch (this.operation) {
            case '&&':
                return left && this.right.evaluate(scope);
            case '||':
                return left || this.right.evaluate(scope);
            default:
                break;
        }
        const right = this.right.evaluate(scope);
        switch (this.operation) {
            case '==':
                return left == right;
            case '===':
                return left === right;
            case '!=':
                return left != right;
            case '!==':
                return left !== right;
            default:
                break;
        }
        if (Array.isArray(left)) {
            throw new EvalError(this.left.position, `operation "${this.operation}" not valid on array`);
        }
        else if (typeof left === 'object') {
            throw new EvalError(this.left.position, `operation "${this.operation}" not valid on object`);
        }
        else if (Array.isArray(right)) {
            throw new EvalError(this.right.position, `operation "${this.operation}" not valid on array`);
        }
        else if (typeof right === 'object') {
            throw new EvalError(this.right.position, `operation "${this.operation}" not valid on object`);
        }
        try {
            switch (this.operation) {
                case '+':
                    return left + right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '-':
                    return left - right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '*':
                    return left * right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '/':
                    return left / right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '%':
                    return left % right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '<':
                    return left < right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '>':
                    return left > right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '<=':
                    return left <= right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '>=':
                    return left >= right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '^':
                    return left ^ right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '**':
                    return Math.pow(left, right); // eslint-disable-line @typescript-eslint/no-explicit-any
                case '<<':
                    return left << right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '>>':
                    return left >> right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '>>>':
                    return left >>> right; // eslint-disable-line @typescript-eslint/no-explicit-any
                case '??':
                    return (_a = left) !== null && _a !== void 0 ? _a : right; // eslint-disable-line @typescript-eslint/no-explicit-any
                default:
                    break;
            }
        }
        catch (error) {
            const message = (_b = (error instanceof Error ? error : {}).message) !== null && _b !== void 0 ? _b : `unknown error with "${this.operation}" operation`;
            throw new EvalError(this.position, message);
        }
        throw new EvalError(this.position, `operation "${this.operation}" not supported`);
    }
}
export class CallFunction extends NonAssignableNode {
    constructor(position, func, args) {
        super();
        this.position = position;
        this.func = func;
        this.args = args;
        this.type = 'callFunction';
    }
    evaluate(scope) {
        const func = this.func.evaluate(scope);
        if (typeof func !== 'function') {
            throw new EvalError(this.position, `expression is not a function`);
        }
        return func(...this.args.map(arg => arg.evaluate(scope)));
    }
}
export class CallMember extends NonAssignableNode {
    constructor(position, object, name, args) {
        super();
        this.position = position;
        this.object = object;
        this.name = name;
        this.args = args;
        this.type = 'callMember';
    }
    evaluate(scope) {
        const object = this.object.evaluate(scope);
        let func = undefined; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (object !== null && object !== undefined) {
            if (typeof object === 'string') {
                if (this.name === 'charCodeAt') {
                    func = object.charCodeAt.bind(object);
                }
            }
            else if (typeof object === 'number') {
                if (this.name === 'toString') {
                    func = object.toString.bind(object);
                }
            }
            else if (Array.isArray(object) || typeof object === 'object') {
                func = object[this.name];
            }
        }
        if (typeof func !== 'function') {
            throw new EvalError(this.position, `expression is not a function`);
        }
        return func(...this.args.map(arg => arg.evaluate(scope)));
    }
}
export class CallScope extends NonAssignableNode {
    constructor(position, name, args) {
        super();
        this.position = position;
        this.name = name;
        this.args = args;
        this.type = 'callScope';
    }
    evaluate(scope) {
        const func = scope[this.name];
        if (typeof func !== 'function') {
            throw new EvalError(this.position, `expression is not a function`);
        }
        return func(...this.args.map(arg => arg.evaluate(scope)));
    }
}
export class Conditional extends NonAssignableNode {
    constructor(position, condition, yes, no) {
        super();
        this.position = position;
        this.condition = condition;
        this.yes = yes;
        this.no = no;
        this.type = 'conditional';
    }
    evaluate(scope) {
        return !!this.condition.evaluate(scope)
            ? this.yes.evaluate(scope)
            : this.no.evaluate(scope);
    }
}
export class LiteralArray extends NonAssignableNode {
    constructor(position, value) {
        super();
        this.position = position;
        this.value = value;
        this.type = 'literalArray';
    }
    evaluate(scope) {
        return this.value.map(el => el.evaluate(scope));
    }
}
export class LiteralObject extends NonAssignableNode {
    constructor(position, value) {
        super();
        this.position = position;
        this.value = value;
        this.type = 'literalObject';
    }
    evaluate(scope) {
        return Object.fromEntries(Object.entries(this.value).map(e => [e[0], e[1].evaluate(scope)]));
    }
}
export class LiteralPrimitive extends AssignableNode {
    constructor(position, value) {
        super();
        this.position = position;
        this.value = value;
        this.type = 'literalPrimitive';
    }
    assign(scope, value) {
        throw new EvalError(this.position, `can't assign to literal primitive`);
    }
    evaluate(scope) {
        return this.value;
    }
}
export class PrefixNot extends NonAssignableNode {
    constructor(position, operation, expression) {
        super();
        this.position = position;
        this.operation = operation;
        this.expression = expression;
        this.type = 'prefixNot';
    }
    evaluate(scope) {
        return !this.expression.evaluate(scope);
    }
}
export class StringInterpolation extends AssignableNode {
    constructor(position, parts) {
        super();
        this.position = position;
        this.parts = parts;
        this.type = 'stringInterpolation';
    }
    assign(scope, value) {
        throw new EvalError(this.position, `can't assign to string interpolation`);
    }
    evaluate(scope) {
        const parts = [];
        for (const part of this.parts) {
            const value = part.evaluate(scope);
            parts.push(typeof value === 'string' ? value : JSON.stringify(value));
        }
        return parts.join('');
    }
}
